import { useCallback, useEffect, useRef, useState } from 'react';
import { submitEnquiry, type Enquiry } from '@/lib/api';

export type FormField = keyof Enquiry;
export type FormErrors = Partial<Record<FormField, string>>;
export type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const EMPTY: Enquiry = {
  name: '',
  email: '',
  company: '',
  interest: 'Build',
  budget: 'Not sure yet',
  message: '',
};

// Deliberately forgiving: enough to catch typos, not enough to reject a valid address.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Pure validation, so the rules can be unit-tested without rendering React. */
export function validate(values: Enquiry): FormErrors {
  const errors: FormErrors = {};

  if (values.name.trim().length < 2) errors.name = 'Please tell us your name.';
  if (!EMAIL.test(values.email.trim())) errors.email = 'A valid email address, so we can reply.';
  if (values.message.trim().length < 20) errors.message = 'A couple of sentences about the problem, please.';

  return errors;
}

/**
 * Owns all contact-form state: values, validation, submission and the
 * resulting status. The Contact section stays presentational.
 */
export function useContactForm() {
  const [values, setValues] = useState<Enquiry>(EMPTY);
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<FormStatus>('idle');
  const controller = useRef<AbortController | null>(null);

  // An in-flight request must not resolve into an unmounted component.
  useEffect(() => () => controller.current?.abort(), []);

  const setField = useCallback((field: FormField, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    // Clear the error as soon as the visitor starts fixing the field.
    setErrors((current) => {
      if (!(field in current)) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
    setStatus((current) => (current === 'error' ? 'idle' : current));
  }, []);

  const submit = useCallback(async () => {
    const found = validate(values);
    setErrors(found);

    const firstInvalid = Object.keys(found)[0];
    if (firstInvalid) {
      document.getElementById(`field-${firstInvalid}`)?.focus();
      return;
    }

    controller.current?.abort();
    controller.current = new AbortController();
    setStatus('submitting');

    try {
      await submitEnquiry(values, controller.current.signal);
      setStatus('success');
      setValues(EMPTY);
    } catch (error) {
      if (controller.current.signal.aborted) return; // unmounted or superseded
      if (import.meta.env.DEV) console.error(error);
      setStatus('error');
    }
  }, [values]);

  const reset = useCallback(() => {
    setValues(EMPTY);
    setErrors({});
    setStatus('idle');
  }, []);

  return { values, errors, status, setField, submit, reset } as const;
}
